// Reading a business card's NFC chip (Web NFC, Chrome on Android). Shown only
// where the browser can; tapping "Read an NFC card" waits for a card, and the
// first vCard on it (a text/vcard record, or text holding one) is submitted
// to make the business card and its lead, as a photo would.
import { Controller } from "@hotwired/stimulus";

export default class NfcReadController extends Controller<HTMLFormElement> {
  static override targets = ["section", "vcard", "status", "button"];

  declare readonly sectionTarget: HTMLElement;
  declare readonly vcardTarget: HTMLInputElement;
  declare readonly statusTarget: HTMLElement;
  declare readonly buttonTarget: HTMLButtonElement;

  private scanning: AbortController | null = null;

  override connect(): void {
    if ("NDEFReader" in window) this.sectionTarget.classList.replace("hidden", "flex");
  }

  override disconnect(): void {
    this.scanning?.abort();
  }

  async read(): Promise<void> {
    this.scanning?.abort();
    this.scanning = new AbortController();
    const reader = new NDEFReader();

    reader.addEventListener("reading", (event) => this.received(event as NDEFReadingEvent));
    reader.addEventListener("readingerror", () => this.say("unreadable"));

    try {
      await reader.scan({ signal: this.scanning.signal });
      this.buttonTarget.disabled = true;
      this.say("waiting");
    } catch {
      this.say("unavailable");
    }
  }

  private received({ message }: NDEFReadingEvent): void {
    const vcard = vcardIn(message);
    if (!vcard) {
      this.say("noVcard");
      return;
    }

    this.scanning?.abort();
    this.vcardTarget.value = vcard;
    this.say("read");
    this.element.requestSubmit();
  }

  // The status line's messages are its data attributes, translated by the page.
  private say(key: string): void {
    this.statusTarget.textContent = this.statusTarget.dataset[key] ?? "";
    if (key !== "waiting" && key !== "read") this.buttonTarget.disabled = false;
  }
}

function vcardIn(message: NDEFMessage): string | null {
  for (const record of message.records) {
    if (!record.data) continue;

    const text = new TextDecoder(record.encoding ?? "utf-8").decode(record.data);
    const isVcard = record.recordType === "mime" && /^text\/(x-)?vcard/i.test(record.mediaType ?? "");
    if (isVcard || (record.recordType === "text" && text.includes("BEGIN:VCARD"))) return text;
  }
  return null;
}
